import { useState, useEffect, useCallback, useRef } from 'react';
import { Exercise } from '@/models/LearnTypes';
import { ANIMATION_TIMINGS } from '@/constants/animations';

/**
 * Props for the useFlashCardManager hook
 */
export interface UseFlashCardManagerProps {
  /**
   * The current exercise to display
   */
  currentExercise: Exercise;
  
  /**
   * Total number of exercises in the set
   */
  totalExercises?: number;
  
  /**
   * Current exercise number (1-based index)
   */
  exerciseNumber?: number;
  
  /**
   * Callback to move to the next exercise
   * @param result Indicates the result of the card (success/fail)
   * @param isFailed Optional boolean indicating if the card was marked as failed
   * @param skipProgressUpdate Optional flag to indicate progress should not be updated
   */
  onNext: (result: { success: boolean; failed?: boolean; skipProgressUpdate?: boolean }) => void;
  
  /**
   * Optional flag to indicate if the answer should already be shown
   * @default false
   */
  showAnswer?: boolean;
  
  /**
   * Optional callback when a card is marked as failed
   * Used to log activity and handle failed cards
   */
  onCardFailed?: (exercise: Exercise) => void;
}

/**
 * All possible animation states for flashcards
 * Using an enum ensures type safety and consistency across the application
 */
export enum AnimationState {
  IDLE = 'idle',
  FLIPPING = 'flipping',
  EXCELLENT = 'excellent',
  EXITING = 'exiting',
  RETURNING = 'returning'
}

/**
 * Custom hook that manages the state and animations for flash cards
 * 
 * This hook handles:
 * 1. Card flip state
 * 2. Animation sequences and timing
 * 3. Card stack generation
 * 4. User interactions (flip, swipe, next)
 * 5. Manually flipped card tracking
 */
export const useFlashCardManager = ({
  currentExercise,
  totalExercises,
  exerciseNumber,
  onNext,
  showAnswer = false,
  onCardFailed
}: UseFlashCardManagerProps) => {
  // Core states
  const [isFlipped, setIsFlipped] = useState(false);
  
  // Animation states
  const [animationState, setAnimationState] = useState<AnimationState>(AnimationState.IDLE);
  
  // Tracking if excellent animation should be shown
  const [showExcellentAnimation, setShowExcellentAnimation] = useState(false);
  
  // Tracking manually flipped cards - now just for UX, not for determining success/failure
  const [manuallyFlippedCards, setManuallyFlippedCards] = useState<string[]>([]);
  
  // Tracking failed cards separately
  const [failedCards, setFailedCards] = useState<string[]>([]);
  
  // Reset isFlipped when moving to a new card or when showAnswer changes
  useEffect(() => {
    setIsFlipped(showAnswer);
  }, [currentExercise?.id, showAnswer]);
  
  // Animation states are now fully handled via the AnimationState enum
  
  // Generate stacked cards for visual effect
  const generateStackedCards = useCallback(() => {
    // Create an array of cards with decreasing opacity and size
    const cards = [];
    const MAX_CARDS = 3;
    
    for (let i = 0; i < MAX_CARDS; i++) {
      cards.push({
        scale: 1 - (i * 0.05), // Each card is 5% smaller
        offset: i * 4, // Each card is offset by 4px
        opacity: 1 - (i * 0.2), // Each card is 20% more transparent
      });
    }
    
    return cards;
  }, []);
  
  // Generated stacked cards for visual effect
  const stackedCards = generateStackedCards();
  
  // Handle card flip - now just flips the card without tracking failure
  const handleCardFlip = useCallback(() => {
    if (animationState !== AnimationState.IDLE && animationState !== AnimationState.FLIPPING) return;
    
    // Still track manually flipped cards for UX purposes
    if (!isFlipped && currentExercise) {
      setManuallyFlippedCards(prev => {
        if (!prev.includes(currentExercise.id)) {
          return [...prev, currentExercise.id];
        }
        return prev;
      });
    }
    
    setAnimationState(AnimationState.FLIPPING);
    setIsFlipped(prev => !prev);
    
    // Reset animation state after flip transition completes
    setTimeout(() => {
      setAnimationState(AnimationState.IDLE);
    }, ANIMATION_TIMINGS.FLIP_DURATION);
  }, [animationState, isFlipped, currentExercise]);
  
  // Handle transition to next card
  const handleNext = useCallback(() => {
    // Early return if animations are already in progress
    if (animationState !== AnimationState.IDLE && animationState !== AnimationState.EXCELLENT) return;
    
    // If this is the "I know this word" button (card isn't flipped yet)
    if (!isFlipped) {
      // Show the excellent animation when "I know this word" is clicked
      setShowExcellentAnimation(true);
      setAnimationState(AnimationState.EXCELLENT);
      
      // First flip the card to show the answer
      setIsFlipped(true);
      
      // Hide excellent animation after delay and begin exit sequence
      setTimeout(() => {
        setShowExcellentAnimation(false);
        setAnimationState(AnimationState.EXITING);
        
        // After exit animation completes, trigger the next card
        setTimeout(() => {
          // Card is successful when "I know this word" is clicked
          onNext({ success: true });
          
          // Reset states for next card
          setIsFlipped(false);
          setAnimationState(AnimationState.IDLE);
        }, ANIMATION_TIMINGS.SWIPE_DURATION);
      }, ANIMATION_TIMINGS.EXCELLENT_DISPLAY);
    } else {
      // Card is already flipped, just exit without excellent animation
      setAnimationState(AnimationState.EXITING);
      
      // After exit animation completes, trigger the next card
      setTimeout(() => {
        // When continue button is clicked after flipping, it's always successful
        onNext({ success: true });
        
        // Reset states for next card - must set the animation state to IDLE first
        // to ensure any new cards can properly animate
        setAnimationState(AnimationState.IDLE);
        setIsFlipped(false);
      }, ANIMATION_TIMINGS.SWIPE_DURATION);
    }
  }, [animationState, onNext, isFlipped]);
  
  // Swipe functionality has been removed
  
  // Handler for when a card is marked as failed ("Practice more" button)
  const handleFail = useCallback(() => {
    // Early return if animations are already in progress - except allow if in FLIPPING state
    // This is important for ensuring animations work correctly when misspelling the last word
    if (animationState !== AnimationState.IDLE && 
        animationState !== AnimationState.EXCELLENT && 
        animationState !== AnimationState.FLIPPING) return;
    
    if (currentExercise) {
      // Add to failed cards list for tracking purposes (no recycling)
      setFailedCards(prev => {
        if (!prev.includes(currentExercise.id)) {
          return [...prev, currentExercise.id];
        }
        return prev;
      });
      
      // Call the optional onCardFailed callback for activity logging
      if (onCardFailed) {
        onCardFailed(currentExercise);
      }
    }
    
    // For cards that aren't flipped yet, we need to show the flip animation
    if (!isFlipped) {
      // Set flipped state first
      setIsFlipped(true);
      
      // Use proper FLIPPING animation state
      setAnimationState(AnimationState.FLIPPING);
      
      // Wait for flip animation to complete before showing exit animation
      setTimeout(() => {
        // Start exit animation
        setAnimationState(AnimationState.EXITING);
        
        // After exit animation completes
        setTimeout(() => {
          // Note: We're keeping 'failed: true' for analytics purposes
          // even though we don't recycle anymore
          onNext({ success: false, failed: true, skipProgressUpdate: true });
          
          // Reset states for next card - must set the animation state to IDLE first
          // to ensure any new cards can properly animate
          setAnimationState(AnimationState.IDLE);
          setIsFlipped(false);
        }, ANIMATION_TIMINGS.SWIPE_DURATION);
      }, ANIMATION_TIMINGS.FLIP_DURATION);
    } else {
      // Card is already flipped, just show exit animation
      setAnimationState(AnimationState.EXITING);
      
      // Wait for exit animation to complete
      setTimeout(() => {
        // We still track failed status for analytics but don't recycle
        onNext({ success: false, failed: true, skipProgressUpdate: true });
        
        // Reset states for next card - must set the animation state to IDLE first
        // to ensure any new cards can properly animate
        setAnimationState(AnimationState.IDLE);
        setIsFlipped(false);
      }, ANIMATION_TIMINGS.SWIPE_DURATION);
    }
  }, [animationState, currentExercise, onNext]);
  
  // Function to check if a card was manually flipped (now just for UX purposes)
  const checkForManualFlip = useCallback(() => {
    const wasFlipped = currentExercise ? manuallyFlippedCards.includes(currentExercise.id) : false;
    return wasFlipped;
  }, [currentExercise, manuallyFlippedCards]);
  
  // Reset flip state - useful when transitioning between cards
  const resetFlipState = useCallback(() => {
    setIsFlipped(false);
  }, []);
  
  // Check if current card was manually flipped
  const wasManuallyFlipped = currentExercise ? manuallyFlippedCards.includes(currentExercise.id) : false;
  
  // Check if current card was marked as failed
  const wasFailed = currentExercise ? failedCards.includes(currentExercise.id) : false;
  
  return {
    // Core functionality
    isFlipped,
    setIsFlipped,
    handleCardFlip,
    handleNext,
    handleFail,
    
    // New animation system
    animationState,
    
    // Visual elements
    stackedCards,
    showExcellentAnimation,
    
    // Card tracking
    manuallyFlippedCards,
    wasManuallyFlipped,
    failedCards,
    wasFailed,
    checkForManualFlip,
    resetFlipState
  };
};
